import axios, { AxiosError } from "axios";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ApiConstants from "../../api-constants";
import { CompetitionMaster } from "../../Types/CompetitionMaster";

const fetchCompetitionMasters = async (
  searchStr: string,
  skipCount: number = 0
) => {
  const res = await axios.get(ApiConstants.competitionMaster, {
    headers: {
      Authorization: ApiConstants.authToken,
      "X-Parse-Application-Id": ApiConstants.parseApplicationId,
      "X-Parse-Client-Key": ApiConstants.parseClientKey,
      "X-Parse-Master-Key": ApiConstants.parseMasterKey,
    },
    params: {
      include: "tees,secretHoles",
      order: "-startDate",
      limit: 10,
      skip: skipCount,
      ...(searchStr
        ? {
            where: JSON.stringify({
              name: { $regex: searchStr, $options: "i" },
            }),
          }
        : {}),
    },
  });

  if (res.status === 200) {
    return res.data.results;
  } else {
    throw new AxiosError(`Unexpected status code: ${res.status}`);
  }
};
const CompetitionListingPage: React.FC = () => {
  const [list, setList] = useState<CompetitionMaster[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [skip, setSkip] = useState<number>(0);
  const observer = useRef<IntersectionObserver>();

  const lastElementRef = useCallback(
    (node: HTMLLIElement) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreCompetitions();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const loadMoreCompetitions = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const data = await fetchCompetitionMasters(searchQuery, skip);
      if (data.length < 10) {
        setHasMore(false);
      }
      setList((prevList) => [...prevList, ...data]);
      setSkip((prevSkip) => prevSkip + data.length);
    } catch (error) {
      console.error("Error loading more competitions:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await fetchCompetitionMasters(searchQuery, 0);
        setList(data);
        setSkip(data.length);
        setHasMore(data.length >= 10);
      } catch (error) {
        console.error("Error fetching competitions:", error);
      }
      setLoading(false);
    };

    // Reset pagination when search query changes
    setSkip(0);
    setHasMore(true);
    fetchData();
  }, [searchQuery]);

  const navigate = useNavigate();
  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={styles.container}>
        <h1 style={styles.title}>Competition List</h1>
        <input
          type="text"
          placeholder="Search competitions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchBox}
        />
        <ul style={styles.list}>
          {list.length > 0
            ? list.map((competition, index) => {
                const startDate = new Date(
                  competition.startDate?.iso || ""
                ).toLocaleDateString("en-GB");
                const venue = competition.golfCourse?.gcName;
                const isLastElement = index === list.length - 1;
                return (
                  <li
                    key={competition.objectId}
                    style={styles.listItem}
                    ref={isLastElement ? lastElementRef : undefined}
                  >
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "1.5rem",
                        fontWeight: 600,
                        padding: "10px 20px",
                        paddingTop: 0,
                        color: "#212121",
                      }}
                    >
                      {competition.name}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: 500,
                          color: "#212121",
                          flex: 1,
                        }}
                      >
                        Date: {startDate}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "end",
                          flex: 1,
                        }}
                      >
                        <div
                          style={{
                            fontSize: "1rem",
                            fontWeight: 500,
                            color: "#212121",
                            textAlign: "end",
                          }}
                        >
                          Venue: {venue}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        style={styles.button}
                        onClick={() => {
                          navigate(
                            `/competition-score/${competition.objectId}`
                          );
                        }}
                      >
                        Scoreboard
                      </button>
                      <button
                        style={{
                          ...styles.button,
                          ...{
                            background: "none",
                            border: "1px solid #F34545",
                            color: "#F34545",
                          },
                        }}
                        onClick={() => {
                          navigate(
                            `/competition-leaderboard/${competition.objectId}`
                          );
                        }}
                      >
                        Leaderboard
                      </button>
                    </div>
                  </li>
                );
              })
            : !loading && (
                <p style={styles.noResults}>No competitions found.</p>
              )}
          {loading && (
            <div style={styles.loadingContainer}>
              <div style={styles.loader}></div>
              <p style={styles.loadingText}>Loading more competitions...</p>
            </div>
          )}
        </ul>
      </div>
    </>
  );
};

export default CompetitionListingPage;
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: 550,
    margin: "0 auto",
    padding: "20px",
    fontFamily: "Arial, sans-serif",
    display: "flex",
    flexDirection: "column",
  },
  title: {
    textAlign: "center",
    marginBottom: "20px",
  },
  searchBox: {
    padding: "10px",
    flex: 1,
    marginBottom: "20px",
    fontSize: "16px",
    borderRadius: "4px",
    border: "1px solid #ccc",
  },
  list: {
    listStyleType: "none",
    padding: 0,
  },
  listItem: {
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "20px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    backgroundColor: "#fff",
  },
  noResults: {
    textAlign: "center",
    color: "gray",
  },
  button: {
    marginTop: "10px",
    padding: "10px 15px",
    fontSize: "14px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "#F34545",
    color: "#fff",
    flex: 1,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
  },
  loader: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #F34545",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    marginTop: "10px",
    color: "#666",
    fontSize: "14px",
  },
};
